'use strict';

/**
 * Webduct Configuration (WebductConfiguration.js).
 * Webduct Host Configuration Helper.
 *
 * @version
 * 2.53.0 LIBENV-232 Update environment to support spooling API URL
 * 2.49.0 LIBENV-223 Add getManufacturingApiPath()
 * 2.44.0 LIBENV-210 Remove orderAdminApi
 * 2.43.0 LIBENV-208 Add getCartApiPath()
 * 2.35.0 LIBENV-184 Add getProductApiPath() and permission/url for Product UI
 * 1.29.0 LIBENV-92 Add getDataExportApiPath
 * 1.27.0 LIBENV-88 Add getPublicApiPath and getDefaultCamSystem
 * 1.15.0 LIBENV-53 Add WebductConfiguration into globalThis for ES6 usage
 * 1.5.0 LIBENV-41 Add getOrderApiPath to WebductConfiguration
 * 1.4.5 LIBENV-37 Add getUserApiPath to Webduct Configuration
 * 1.3.1 LIBENV-21 Add Environment Dashboard API Path
 * 1.0.0 LIBENV-1 Initialize environment library
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Erik Orozco <eorozco@intersog.com>
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @copyright (c) 2018-2023 Build Centrix
 */
var WebductConfiguration = function () {

    // Controller
    var helper = {};

    // Configuration
    var configuration = {};

    /************************************** External Functionality ****************************************************/

    /**
     * Get Authentication Timeout Limit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getAuthenticationTimeoutLimit = function () {
        return getHostConfiguration('authenticationTimeout');
    };

    /**
     * Get API Base Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getBaseUrl = function () {
        return getHostConfiguration('baseUrl');
    };

    /**
     * Get API Base Path
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getBaseApiPath = function () {
        return getHostConfiguration('baseApiUrl');
    };

    /**
     * Get Cart API Path
     *
     * @since 2.43.0
     * @returns {String}
     */
    helper.getCartApiPath = function () {
        return getHostConfiguration('cartApiUrl');
    };

    /**
     * Get Cookie Domain
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCookieDomain = function () {
        return getHostConfiguration('cookieDomain');
    };

    /**
     * Get Calendar Refresh Seconds
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCalendarRefreshSeconds = function () {
        return getHostConfiguration('refreshSeconds');
    };

    /**
     * Get Dashboard API Path
     *
     * @since 1.3.1
     * @returns {String}
     */
    helper.getDashboardApiPath = function () {
        return getHostConfiguration('dashboardApiUrl');
    };

    /**
     * Get Data Export API Path
     *
     * @since 1.29.0
     * @returns {String}
     */
    helper.getDataExportApiPath = function () {
        return getHostConfiguration('dataExportApiUrl');
    };

    /**
     * Get Number of Days Orders to Load Before
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getDaysBeforeOrderLoadLimit = function () {
        return getHostConfiguration('loadOrdersDaysBefore');
    };

    /**
     * Get Number of Days Orders to Load After
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getDaysAfterOrderLoadLimit = function () {
        return getHostConfiguration('loadOrdersDaysAfter');
    };

    /**
     * Get Default Thumbnail Size
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getDefaultThumbnailSize = function () {
        return getHostConfiguration('defaultThumbnailSize');
    };

    /**
     * Get I18n Settings
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getI18nSettings = function() {
        return getHostConfiguration('i18n');
    };

    /**
     * Get Job Center API Path
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getJobCenterApiPath = function () {
        return getHostConfiguration('jobCenterApiUrl');
    };

    /**
     * Get Manufacturing Admin API Path
     *
     * @since 2.49.0
     * @returns {String}
     */
    helper.getManufacturingAdminApiPath = function () {
        return getHostConfiguration('manufacturingAdminApiUrl');
    };

    /**
     * Get Maximum File Post Size Limit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getMaximumFilePostSize = function () {
        return getHostConfiguration('maximumPostSize');
    };

    /**
     * Get Maximum File Size for Uploads
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getMaximumFileSize = function () {
        return getHostConfiguration('maximumFileSize');
    };

    /**
     * Get Maximum File Uploads Count
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getMaximumFileUploads = function () {
        return getHostConfiguration('maximumFileUploads');
    };

    /**
     * Get Maximum Notification Display Limit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getMaximumNotificationDisplayLimit = function () {
        return getHostConfiguration('maxNotifications');
    };

    /**
     * Get Maximum Thumbnail Size
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getMaximumThumbnailSize = function () {
        return getHostConfiguration('maxThumbnailSize');
    };

    /**
     * Get Oauth2 Authentication Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getOauth2AuthUrl = function () {
        return getHostConfiguration('oAuth2AuthUrl');
    };

    /**
     * Get Oauth2 Base Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getOauth2BaseUrl = function () {
        return getHostConfiguration('oauth2ApiBaseUrl');
    };

    /**
     * Get Oauth2 Authentication Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getOauth2AuthenticationUrl = function () {
        return getHostConfiguration('oauth2AuthenticationUrl');
    };

    /**
     * Get Oauth2 Sso Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getOauth2SsoUrl = function () {
        return getHostConfiguration('oauth2SsoUrl');
    };

    /**
     * Get Order API Path
     *
     * @since 1.5.0
     * @returns {String}
     */
    helper.getOrderApiPath = function () {
        return getHostConfiguration('orderApiUrl');
    };

    /**
     * Get Ordering API Path
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getOrderingApiPath = function () {
        return getHostConfiguration('orderingApiUrl');
    };

    /**
     * Get Product API Base Path
     *
     * @since 2.35.0
     * @returns {String}
     */
    helper.getProductApiPath = function () {
        return getHostConfiguration('productApiUrl');
    };

    /**
     * Get Public API Path
     *
     * @since 1.27.0
     * @returns {String}
     */
    helper.getPublicApiPath = function () {
        return getHostConfiguration('publicApiUrl');
    };

    /**
     * Get Redirect Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getRedirectUrl = function () {
        return getHostConfiguration('urlRedirectBase');
    };

    /**
     * Get Reload Jobs Frequency (ms)
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getReloadJobsFrequency = function () {
        return getHostConfiguration('loadJobsTimer');
    };

    /**
     * Get Reload Notifications Frequency (ms)
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getReloadNotificationsFrequency = function () {
        return getHostConfiguration('notificationsTimer');
    };

    /**
     * Get Service Updates RSS Feed Url
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getRssFeedUrl = function () {
        return getHostConfiguration('serviceUpdateRssFeedUrl');
    };


    /**
     * Get Spooling API Path
     *
     * @since 2.53.0
     * @returns {String}
     */
    helper.getSpoolingApiPath = function () {
        return getHostConfiguration('spoolingApiUrl');
    };

    /**
     * Get Timecard Frequent User Limit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getTimecardFrequentUserLimit = function () {
        return getHostConfiguration('frequentUsers');
    };

    /**
     * Get Tracker API Path
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getTrackerApiPath = function () {
        return getHostConfiguration('trackerApiUrl');
    };

    /**
     * Get User API Path
     *
     * @since 1.4.5
     * @returns {String}
     */
    helper.getUserApiPath = function () {
        return getHostConfiguration('userApiUrl');
    };

    /****************************************** Initialization ********************************************************/

    /**
     * Set Configuration
     *
     * @since 1.0.0
     * @param {Object} input
     */
    helper.setConfiguration = function (input) {
        configuration = input;
    };

    /***************************************** Private Functions ******************************************************/

    /**
     * Get Host Configuration Value by Key
     *
     * @since 1.0.0
     * @param {String} key
     * @returns {String}
     */
    function getHostConfiguration(key) {
        return configuration[key]
    }

    // Return
    return helper;

};
globalThis.WebductConfiguration = WebductConfiguration;
// EOF
